import React, {useState, useEffect} from 'react'
import {useIntl} from 'react-intl'
import {Field, useFormikContext} from 'formik'
import {Brand} from '../../../../../brands/core/_models'
import {CustomSelect, Checkbox} from '@/components/form'
import {Regions} from '../../Regions'
import {Product} from '../../../../core/_models'
import {getProductAliases} from '../../../../core/_requests'
import {Card} from '@/components/Card'

type Props = {
  brands: Brand[]
  types: string[]
  categories: string[]
  platforms: string[]
}

const DetailsCard: React.FC<Props> = ({brands, types, categories, platforms}) => {
  const intl = useIntl()
  const { values, setFieldValue } = useFormikContext();
  const product = values as Product
  const [isAlias, setIsAlias] = useState<boolean>(!!product?.alias)
  const [aliases, setAliases] = useState<Array<Product>>([])
  const [error, setError] = useState<string|undefined>()

  const aliasMatch = async () => {
    try {
      setError(undefined)
      setAliases(await getProductAliases(product._id))
    } catch (e: unknown) {
      setIsAlias(false)
      setError('You must register the product before activating an alias.')
    }
  }

  useEffect(() => {
    void(isAlias === true && aliasMatch())
  }, [isAlias])

  return (
    <Card
      title='Product Details'
      collapsable={false}
      className="card py-4"
    >
      <div>
        <Field
          id='isAlias'
          name="isAlias"
          component={Checkbox}
          label="Is Alias ?"
          onChange={() => {
            setIsAlias(!isAlias)
            setFieldValue('alias', null)
          }}
          defaultChecked={isAlias}
        />

        {error && (
          <p className="fw-bold text-danger">{error}</p>
        )}

        {error === undefined && isAlias && (
          <>
            {aliases.length > 0 ? (
              <Field
                default="Select product"
                component={CustomSelect}
                options={aliases.map((p: Product) => ({
                  value: p._id, label: `${p.credits}Cr - ${p.name}`
                }))}
                name="alias"
                id="alias"
                required={isAlias}
              />
            ) : (
              <p className="fw-bold text-danger">No product match</p>
            )}
          </>
        )}
      </div>

      <div className='separator separator-dashed my-5'></div>

      <Regions />
      <div className='separator separator-dashed my-5'></div>
      <Field
        default='Select Brand'
        component={CustomSelect}
        options={(brands.map((b: Brand) => ({
          value: b._id, label: b.name
        })))}
        label="Product brand"
        name="brand._id"
        id="brand"
        required={false}
      />
      <div className="text-muted fs-7 mb-7">Set product brand.</div>

      <div className='separator separator-dashed my-5'></div>

      <Field
        default="Select type"
        component={CustomSelect}
        options={types.map((t: string) => ({
          value: t, label: intl.formatMessage({id: `P2C.PRODUCT_TYPE_${t}`})
        }))}
        label="Product type"
        name="type"
        id="type"
        required={true}
      />

      <div className="text-muted fs-7 mb-7">Set product to a type.</div>

      <div className='separator separator-dashed my-5'></div>

      <Field
        default="Select category"
        component={CustomSelect}
        options={categories.map((c: string) => ({
          value: c, label: intl.formatMessage({id: `P2C.PRODUCT_CATEGORY_${c}`})
        }))}
        label="Product category"
        name="category"
        required={true}
      />

      <div className='separator separator-dashed my-5'></div>

      <Field
        default="Select platform"
        component={CustomSelect}
        options={platforms.map((p: string) => ({
          value: p, label: p.split('_').at(-1)
        }))}
        label="Product platform"
        name="platform"
        required={true}
      />



      <div className='separator separator-dashed my-5'></div>

      <label className="form-label d-block">Tags</label>
      <div className="text-muted fs-7">Add tags to a product.</div>
      <a href="apps/ecommerce/catalog/add-category.html" className="btn btn-light-primary btn-sm mb-10">
        <i className="ki-duotone ki-plus fs-2"></i>
        Create new tag
      </a>
    </Card>
  )
}

export {DetailsCard}
